import React, { Component } from 'react';
import PropTypes from 'prop-types';
import ToolTipIcon from '../ToolTipIcon/ToolTipIcon';
import TagCloud from '../TagCloud/TagCloud';
import SequenceEntriesTable from './SequenceEntriesTable';
import './SequenceEntriesTable.css';
import { getPermissions } from '../../utils/getPermissions';

export default class SequenceForm extends Component {
  state = {
    showErrors: false,
    errors: {
      name: null,
    },
    name: this.props.sequence?.name || '',
    description: this.props.sequence?.description || '',
  };

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { showErrors } = this.state;

    if (showErrors) {
      this.validate(nextProps);
    }
  }

  componentDidUpdate(prevProps) {
    const { sequence } = this.props;

    if (sequence && sequence !== prevProps.sequence) {
      this.setState({
        name: sequence.name || '',
        description: sequence.description || '',
      });
    }
  }

  onChange = event => {
    const { onChange } = this.props;

    onChange(event.target.name, event.target.value);
  };

  onChangeTag = tags => {
    const { onChange } = this.props;

    onChange('tags', tags);
  };

  errorClass = error => (error ? 'error' : null);

  validate = nextProps => {
    const props = nextProps || this.props;
    const {
      sequence: { name },
    } = props;
    const errors = { name: null };
    let valid = true;

    // name
    if (!name.trim()) {
      errors.name = 'This field cannot be empty.';
      valid = false;
    } else if (this.isNameInUse(props)) {
      errors.name = 'This name is already in use.';
      valid = false;
    }

    this.setState({ showErrors: true, errors });

    return valid;
  };

  submit = event => {
    const { isNew, onAdd, onUpdate } = this.props;

    event.preventDefault();

    if (!this.validate()) {
      return;
    }

    if (isNew) {
      onAdd();
    } else {
      onUpdate();
    }
  };

  isNameInUse = props => {
    const { sequences, sequence } = props;

    return sequences.some(item => {
      const name = sequence.name.toLowerCase().trim();
      const itemName = item.name.toLowerCase().trim();

      return +item.id !== +sequence.id && itemName === name;
    });
  };

  getButtons = () => {
    const { auth, isNew, onCancel } = this.props;
    const p = getPermissions(auth);
    const add = (
      <button
        key="1"
        type="submit"
        data-test="button-add"
        className="flex-1 bg-btn-primary-form text-white px-8 py-4 rounded-2xl font-bold uppercase tracking-widest text-xs hover:scale-[1.02] active:scale-[0.98] transition-all shadow-lg shadow-black/20"
      >
        Add Sequence
      </button>
    );
    const update = (
      <button
        key="2"
        type="submit"
        data-test="button-update"
        className="flex-1 bg-btn-primary-form text-white px-8 py-4 rounded-2xl font-bold uppercase tracking-widest text-xs hover:scale-[1.02] active:scale-[0.98] transition-all shadow-lg shadow-black/20"
      >
        {' '}
        Save Changes
      </button>
    );
    const cancel = (
      <button
        key="3"
        type="button"
        data-test="button-cancel"
        className="px-8 py-4 rounded-2xl font-bold uppercase tracking-widest text-xs text-gray-400 hover:text-black transition-colors border border-transparent hover:border-gray-200"
        onClick={onCancel}
      >
        {' '}
        Cancel
      </button>
    );

    if (!p.writer) {
      return [cancel];
    }

    return isNew ? [add, cancel] : [update, cancel];
  };

  render() {
    const {
      auth,
      users,
      folders,
      sequence,
      onSort,
      onAddEntry,
      onRemoveEntry,
      onChangeRepeat,
      onClickPlaylist,
      onRemovePlaylist,
    } = this.props;
    const { name, description, errors } = this.state;
    const p = getPermissions(auth);

    if (!sequence) {
      return null;
    }

    const { tags, sequenceEntries } = sequence;
    const atLeastStandardTierOrNoEntries = p.atLeastTierPro || !sequenceEntries?.length;

    return (
      <div
        id="edit"
        className="form-signs lg:col-span-8 mt-4 bg-white rounded-[2.5rem] shadow-[0_20px_50px_rgba(0,0,0,0.05)] border border-gray-200 p-4 sequence-form"
      >
        <form className="space-y-6" onSubmit={this.submit}>
          <div className="grid grid-cols-2 gap-4">
            <div className="relative group" data-test="sequence-form-group">
              {this.errorClass(errors) && (
                <span className="text-sunrise font-bold  mt-1 mb-1">{errors.name}</span>
              )}
              <label
                htmlFor="input-name"
                className=" form-label text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 absolute -top-2 left-6 bg-white px-2 transition-all group-focus-within:text-warmth"
              >
                Sequence Name
              </label>
              <input
                data-test="input-name"
                type="text"
                name="name"
                value={name}
                onChange={e => this.setState({ name: e.target.value })}
                onBlur={e => this.onChange(e)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.stopPropagation();
                    this.onChange(e);
                  }
                }}
                disabled={!p.writer}
                className="w-full text-xl font-bold px-2 py-3 rounded-2xl border border-gray-200 focus:border-warmth focus:ring-0 transition-all outline-none"
              />
            </div>

            <div className="relative group" data-test="sequence-form-group">
              <label
                htmlFor="input-desc"
                className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 absolute -top-2 left-6 bg-white px-2 transition-all group-focus-within:text-warmth"
              >
                Description
              </label>
              <textarea
                id="playlist-description"
                data-test="input-desc"
                rows="1"
                placeholder="Enter description"
                value={description || ''}
                onInput={e => {
                  e.target.style.height = 'auto';
                  e.target.style.height = `${e.target.scrollHeight}px`;
                }}
                onChange={e => this.setState({ description: e.target.value })}
                onBlur={e => this.onChange(e)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.stopPropagation();
                    this.onChange(e);
                  }
                }}
                disabled={!p.writer}
                name="description"
                className="w-full text-xl  font-medium px-2 py-3 rounded-2xl border border-gray-200 focus:border-warmth focus:ring-0 transition-all outline-none resize-none"
              />
            </div>

            <TagCloud tags={tags} onChange={this.onChangeTag} disabled={!p.writer} />
          </div>
          {p.writer && atLeastStandardTierOrNoEntries && (
            <ToolTipIcon className="pull-right" toolText="Add Sign" onClick={onAddEntry}>
              <img
                src="/assets/images/icons/upload_icon.svg"
                alt="Add video to playlist"
                className="w-[50px] h-[50px] cursor-pointer mt-2 flex justify-center items-center bg-white rounded-lg"
              />
            </ToolTipIcon>
          )}
          <h4>Sequence Entries</h4>
          <SequenceEntriesTable
            auth={auth}
            users={users}
            folders={folders}
            data={sequenceEntries}
            onChangeRepeat={onChangeRepeat}
            onClickPlaylist={onClickPlaylist}
            onSort={onSort}
            onRemoveEntry={onRemoveEntry}
            onRemovePlaylist={onRemovePlaylist}
          />
          {/* <Metadata data={sequence} users={users} /> */}
          <div className="pt-6 flex flex-col sm:flex-row gap-4">{this.getButtons()}</div>
        </form>
      </div>
    );
  }
}

SequenceForm.propTypes = {
  auth: PropTypes.object.isRequired,
  users: PropTypes.array.isRequired,
  folders: PropTypes.object.isRequired,
  sequences: PropTypes.array,
  sequence: PropTypes.object,
  isNew: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSort: PropTypes.func.isRequired,
  onAddEntry: PropTypes.func.isRequired,
  onRemoveEntry: PropTypes.func.isRequired,
  onChangeRepeat: PropTypes.func.isRequired,
  onRemovePlaylist: PropTypes.func.isRequired,
  onClickPlaylist: PropTypes.func.isRequired,
};
